import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Star } from "lucide-react";
import { InfoTip } from "@/components/InfoTip";
import { PageFilterBox } from "@/components/PageFilterBox";
import { useBrand } from "@/hooks/useBrand";
import { FavoriteModal } from "@/components/FavoriteModal";
import {
  FAVORITES_CHANGED,
  favoriteLabel,
  isFavorite,
} from "@/lib/favorites";

export function PageHead({
  title,
  subtitle,
  actions,
  info,
  clientName,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  info?: string;
  clientName?: string;
}) {
  const brand = useBrand();
  const { pathname, search } = useLocation();
  const url = pathname + search;
  const [starred, setStarred] = useState(() => isFavorite(url));
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (title && brand?.formatTitle) {
      document.title = brand.formatTitle(title);
    }
  }, [title, brand]);

  useEffect(() => {
    const sync = () => setStarred(isFavorite(url));
    sync();
    window.addEventListener(FAVORITES_CHANGED, sync);
    return () => window.removeEventListener(FAVORITES_CHANGED, sync);
  }, [url]);
  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-[22px] font-semibold m-0 flex items-center gap-2">{title}{info && <InfoTip text={info} />}</h1>
          {subtitle && <p className="text-[13px] text-muted-foreground mt-1 m-0">{subtitle}</p>}
        </div>
        {/* The filter box is the far-right element on every page; actions sit
            next to it. */}
        <div className="flex items-center flex-wrap gap-2">
          {actions}
          <button
            type="button"
            aria-label={starred ? "Edit or remove favorite view" : "Save view to favorites"}
            title={starred ? "Edit or remove favorite view" : "Save view to favorites"}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer"
            onClick={() => setModalOpen(true)}
          >
            <Star size={16} aria-hidden="true" className={starred ? "fill-amber-400 text-amber-400" : ""} />
          </button>
          <PageFilterBox />
        </div>
      </div>
      <FavoriteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        url={url}
        defaultLabel={favoriteLabel(title, search)}
        clientName={clientName}
      />
    </>
  );
}

export function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1.5 tabular">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
